import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { ReadableNote } from './ReadableNote';
import { weightChartEntries, weightDisplay } from '../lib/weightDisplay';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { Scale, TrendingUp, TrendingDown, Minus, Plus, Calendar, Edit2, Trash2 } from 'lucide-react';

const WeightMonitoring = ({ athleteId, teamId, isAthleteSelf = false }) => {
  const { api, hasRole, user } = useAuth();
  const { language, t } = useLanguage();
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  
  const [newEntry, setNewEntry] = useState({
    weight_kg: '',
    date: new Date().toISOString().split('T')[0],
    note: ''
  });

  // Permission checks
  const canAdd = isAthleteSelf || hasRole(['ADMIN', 'SNC']);
  const canEdit = hasRole(['ADMIN', 'SNC', 'PHYSIO']);
  const canDelete = hasRole(['ADMIN', 'SNC']);

  const fetchWeightData = useCallback(async () => {
    if (!athleteId) return;
    
    try {
      setLoading(true);
      setLoadError(false);
      const [summaryRes, entriesRes] = await Promise.all([
        api.get(`/weight/athlete/${athleteId}/summary`),
        api.get(`/weight/athlete/${athleteId}?limit=50`)
      ]);
      setSummary(summaryRes.data);
      setEntries(entriesRes.data);
    } catch (err) {
      setLoadError(true);
      console.error('Failed to fetch weight data:', err);
    } finally {
      setLoading(false);
    }
  }, [api, athleteId]);

  useEffect(() => {
    fetchWeightData();
  }, [fetchWeightData]);

  const handleAddEntry = async () => {
    if (!newEntry.weight_kg) {
      toast.error('Please enter a weight');
      return;
    }
    
    try {
      await api.post('/weight', {
        athlete_id: athleteId,
        team_id: teamId,
        date: newEntry.date,
        weight_kg: parseFloat(newEntry.weight_kg),
        note: newEntry.note || null
      });
      toast.success('Weight entry added');
      setShowAddDialog(false);
      setNewEntry({
        weight_kg: '',
        date: new Date().toISOString().split('T')[0],
        note: ''
      });
      fetchWeightData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add weight entry');
    }
  };

  const handleEditEntry = async () => {
    if (!editingEntry) return;
    
    try {
      await api.put(`/weight/entries/${editingEntry.id}`, {
        weight_kg: parseFloat(editingEntry.weight_kg),
        note: editingEntry.note || null
      });
      toast.success('Weight entry updated');
      setShowEditDialog(false);
      setEditingEntry(null);
      fetchWeightData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update weight entry');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!confirm('Delete this weight entry?')) return;
    
    try {
      await api.delete(`/weight/entries/${entryId}`);
      toast.success('Weight entry deleted');
      fetchWeightData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete weight entry');
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === 'UP') return <TrendingUp className="w-4 h-4 text-amber-400" />;
    if (trend === 'DOWN') return <TrendingDown className="w-4 h-4 text-blue-400" />;
    return <Minus className="w-4 h-4 text-zinc-400" />;
  };

  const getTrendColor = (change) => {
    if (!change) return 'text-zinc-400';
    if (change > 0) return 'text-amber-400';
    if (change < 0) return 'text-blue-400';
    return 'text-zinc-400';
  };

  // Simple SVG line chart for weight trend
  const renderChart = () => {
    if (!entries || entries.length < 2) return null;
    
    const chartEntries = weightChartEntries(entries);
    if (chartEntries.length < 2) return null;
    if (chartEntries.length < 2) return null;
    
    const weights = chartEntries.map(e => e.weight_kg);
    const minWeight = Math.min(...weights) - 1;
    const maxWeight = Math.max(...weights) + 1;
    const range = maxWeight - minWeight || 1;
    
    const width = 300;
    const height = 100;
    const padding = 10;
    
    const points = chartEntries.map((entry, i) => {
      const x = padding + (i / (chartEntries.length - 1)) * (width - 2 * padding);
      const y = height - padding - ((entry.weight_kg - minWeight) / range) * (height - 2 * padding);
      return { x, y, weight: entry.weight_kg, date: entry.date };
    });
    
    const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    
    return (
      <div className="mt-4">
        <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
          {/* Grid lines */}
          <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#3f3f46" strokeWidth="1" />
          <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#3f3f46" strokeWidth="1" />
          
          {/* Y-axis labels */}
          <text x="5" y={padding + 5} fill="#71717a" fontSize="8">{maxWeight.toFixed(0)}</text>
          <text x="5" y={height - padding} fill="#71717a" fontSize="8">{minWeight.toFixed(0)}</text>
          
          {/* Trend line */}
          <path d={pathD} fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          
          {/* Data points */}
          {points.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r="3" fill="#f59e0b" stroke="#18181b" strokeWidth="1" />
          ))}
        </svg>
        <div className="flex justify-between text-xs text-zinc-500 mt-1 px-2">
          <span>{chartEntries[0]?.date}</span>
          <span>{chartEntries[chartEntries.length - 1]?.date}</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-6">
          <div className="text-zinc-500 text-center">Loading weight data...</div>
        </CardContent>
      </Card>
    );
  }

  if (loadError) return <p role="alert" className="text-amber-300 text-sm" data-testid="weight-load-error">{t('Unable to load weight measurements', 'Impossibile caricare le misurazioni del peso')}</p>;
  return (
    <Card className="min-w-0 bg-zinc-900/50 border-zinc-800" data-testid="weight-monitoring">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-zinc-100 flex items-center gap-2">
            <Scale className="w-5 h-5 text-amber-500" />
            {t('Weight Monitoring', 'Monitoraggio Peso')}
          </CardTitle>
          {canAdd && (
            <Button
              onClick={() => setShowAddDialog(true)}
              size="sm"
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
              data-testid="add-weight-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Weight Summary */}
        {summary?.latest ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-zinc-950/50 border border-zinc-800 rounded-sm p-3 text-center">
              <div className="text-xl font-bold text-amber-400 break-words" data-testid="latest-weight">{weightDisplay(summary.latest.weight_kg, language)}</div>
              <div className="text-xs text-zinc-500">{t('Latest', 'Ultimo peso')}</div>
            </div>
            <div className="bg-zinc-950/50 border border-zinc-800 rounded-sm p-3 text-center">
              <div className="flex items-center justify-center gap-1">
                {getTrendIcon(summary.trend)}
                <span className={`text-lg font-bold ${getTrendColor(summary.change_kg)}`} data-testid="weight-change">
                  {summary.change_kg > 0 ? '+' : ''}{summary.change_kg ?? '—'}
                </span>
              </div>
              <div className="text-xs text-zinc-500">{t('kg change', 'Variazione kg')}</div>
            </div>
            <div className="bg-zinc-950/50 border border-zinc-800 rounded-sm p-3 text-center">
              <div className="text-lg font-medium text-zinc-300" data-testid="seven-day-avg">
                {summary.seven_day_avg ?? '—'}
              </div>
              <div className="text-xs text-zinc-500">{t('7-day avg', 'Media 7 giorni')}</div>
            </div>
            <div className="bg-zinc-950/50 border border-zinc-800 rounded-sm p-3 text-center">
              <div className="text-sm font-medium text-zinc-300" data-testid="last-date">{summary.latest.date}</div>
              <div className="text-xs text-zinc-500">{t('Last measured', 'Ultima misurazione')}</div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-zinc-500" data-testid="empty-weight-state">
            <Scale className="w-12 h-12 mx-auto mb-3 text-zinc-700" />
            <p className="text-zinc-400">{t('Weight unavailable', 'Peso non disponibile')}</p>
            {canAdd && (
              <p className="text-sm mt-1">{t('No valid measurement recorded', 'Nessuna misurazione valida registrata')}</p>
            )}
          </div>
        )}

        {/* Weight Trend Chart */}
        {renderChart()}

        {/* Weight History */}
        {entries.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-zinc-400 mb-2">{t('History', 'Storico')}</h4>
            <div className="max-h-96 overflow-y-auto space-y-1">
              {entries.map((entry) => (
                <div
                  key={entry.id}
                  className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 p-2 bg-zinc-950/30 rounded-sm text-sm group"
                  data-testid={`weight-history-row-${entry.id}`}
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap gap-x-3 gap-y-1">
                      <span className="text-amber-400 font-mono font-bold" data-testid={`weight-history-value-${entry.id}`}>{weightDisplay(entry.weight_kg, language)}</span>
                      <span className="text-zinc-400">{entry.date}</span>
                    </div>
                    <ReadableNote testId={`weight-note-${entry.id}`} className="mt-2">{entry.note}</ReadableNote>
                  </div>
                  <div className="flex flex-wrap items-start justify-end gap-1 max-w-20">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      entry.source === 'ATHLETE' ? 'bg-blue-500/20 text-blue-400' : 'bg-zinc-700/50 text-zinc-400'
                    }`}>
                      {entry.source}
                    </span>
                    {canEdit && (
                      <button
                        onClick={() => { setEditingEntry(entry); setShowEditDialog(true); }}
                        data-testid={`edit-weight-${entry.id}`}
                        className="p-1 text-zinc-500 hover:text-amber-400"
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => handleDeleteEntry(entry.id)}
                        data-testid={`delete-weight-${entry.id}`}
                        className="p-1 text-zinc-500 hover:text-rose-400"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      {/* Add Weight Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Add Weight Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Weight (kg)</Label>
              <Input
                type="number"
                step="0.1"
                min="30"
                max="200"
                value={newEntry.weight_kg}
                onChange={(e) => setNewEntry({ ...newEntry, weight_kg: e.target.value })}
                placeholder="e.g., 75.5"
                className="bg-zinc-950 border-zinc-800 text-lg font-mono"
                data-testid="weight-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Date</Label>
              <Input
                type="date"
                value={newEntry.date}
                onChange={(e) => setNewEntry({ ...newEntry, date: e.target.value })}
                className="bg-zinc-950 border-zinc-800"
                data-testid="weight-date"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Note (optional)</Label>
              <Textarea
                value={newEntry.note}
                onChange={(e) => setNewEntry({ ...newEntry, note: e.target.value })}
                placeholder="e.g., Post-match weigh-in"
                className="bg-zinc-950 border-zinc-800"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddDialog(false)}
              className="border-zinc-700 text-zinc-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddEntry}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
              data-testid="save-weight-btn"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Weight Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Edit Weight Entry</DialogTitle>
          </DialogHeader>
          {editingEntry && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Weight (kg)</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="30"
                  max="200"
                  value={editingEntry.weight_kg}
                  onChange={(e) => setEditingEntry({ ...editingEntry, weight_kg: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 text-lg font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Date</Label>
                <div className="text-zinc-300 bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2">
                  {editingEntry.date}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Note (optional)</Label>
                <Textarea
                  value={editingEntry.note || ''}
                  onChange={(e) => setEditingEntry({ ...editingEntry, note: e.target.value })}
                  placeholder="e.g., Post-match weigh-in"
                  className="bg-zinc-950 border-zinc-800"
                  rows={2}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShowEditDialog(false); setEditingEntry(null); }}
              className="border-zinc-700 text-zinc-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditEntry}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
            >
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default WeightMonitoring;
